/* eslint-disable @atlassian/tangerine/import/no-parent-imports */

import React from 'react';

import { useIntl } from 'react-intl-next';

import Badge from '@atlaskit/badge';
import Button from '@atlaskit/button/new';
import DropdownMenu from '@atlaskit/dropdown-menu';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, Stack, xcss } from '@atlaskit/primitives';

import { universalTypeFilterId } from '../../../../../common/constants/filters';
import { ProductKeys } from '../../../../../common/constants/products';
import { FilterButton } from '../../../../../common/ui/filters/filter-button';
import {
	useFiltersToDisplay,
	useSearchActions,
	useSelectedFilterCount,
	useSelectedProduct,
} from '../../../../../controllers/store/search';
import { filter as assignee } from '../../../../../controllers/store/search/filters/assignee';
import { filter as content } from '../../../../../controllers/store/search/filters/content';
import { filter as contributor } from '../../../../../controllers/store/search/filters/contributor';
import { filter as label } from '../../../../../controllers/store/search/filters/label';
import { filter as lastUpdated } from '../../../../../controllers/store/search/filters/last-updated';
import { filter as owner } from '../../../../../controllers/store/search/filters/owner';
import { filter as project } from '../../../../../controllers/store/search/filters/project';
import { filter as space } from '../../../../../controllers/store/search/filters/space';
import { filter as titleMatchOnly } from '../../../../../controllers/store/search/filters/title-match-only';
import { filter as verified } from '../../../../../controllers/store/search/filters/verified';
import AssigneeFilter from '../assignee';
import ContentFilter from '../content';
import ContributorFilter from '../contributor';
import LabelFilter from '../label';
import LastUpdatedFilter from '../last-updated';
import OwnerFilter from '../owner';
import ProjectFilter from '../project';
import { ResultsSort } from '../results-sort';
import SpaceFilter from '../space';
import TitleMatchOnlyFilter from '../title-match-only';
import UniversalTypeFilter from '../universal-type';
import VerifiedFilter from '../verified';

import { i18n } from './messages';

const dropdownContainerStyles = xcss({
	padding: 'space.200',
	width: '312px',
});

const filters = {
	[universalTypeFilterId]: <UniversalTypeFilter key={universalTypeFilterId} />,

	[lastUpdated.id]: <LastUpdatedFilter key={lastUpdated.id} />,
	[contributor.id]: <ContributorFilter key={contributor.id} />,
	[owner.id]: <OwnerFilter key={owner.id} />,
	[space.id]: <SpaceFilter key={space.id} />,
	[assignee.id]: <AssigneeFilter key={assignee.id} />,
	[project.id]: <ProjectFilter key={project.id} />,

	[content.id]: <ContentFilter key={content.id} />,
	[titleMatchOnly.id]: <TitleMatchOnlyFilter key={titleMatchOnly.id} isFilterInDropdown />,
	[verified.id]: <VerifiedFilter key={verified.id} />,

	[label.id]: <LabelFilter key={label.id} />,
};

const dividerStyles = xcss({
	borderBottom: '1px',
	borderColor: 'color.border',
	borderStyle: 'solid',
	borderTop: 0,
});

const DropdownFilter = () => {
	const { formatMessage } = useIntl();
	const [{ selectedFilterCount }] = useSelectedFilterCount();
	const [{ filtersInDropdown }] = useFiltersToDisplay();
	const { clearAllAvailableFilters } = useSearchActions();
	const [selectedProduct] = useSelectedProduct();
	const isConfluence = selectedProduct === ProductKeys.Confluence;

	return (
		<DropdownMenu
			trigger={({ triggerRef, isSelected, testId, ...providedProps }) => (
				<FilterButton
					otherProps={{ ...providedProps, ref: triggerRef }}
					isSelected={selectedFilterCount > 0}
					filterOrControlId="dropdown"
					label={formatMessage(i18n.title)}
					selectionBadge={
						selectedFilterCount > 0 ? (
							<Badge appearance="primary">{selectedFilterCount}</Badge>
						) : null
					}
				/>
			)}
		>
			<Stack xcss={dropdownContainerStyles} space="space.200">
				<Stack space="space.200">
					{filtersInDropdown.map((filterId) => {
						// TODO: remove the typecast here
						// (was added so that product type filters didn't have to be included in the filters object)
						return filters[filterId as keyof typeof filters];
					})}
					{isConfluence && <ResultsSort />}
				</Stack>

				<Box as="div" xcss={dividerStyles} />
				<Box>
					<Button onClick={clearAllAvailableFilters}>{formatMessage(i18n.clearAll)}</Button>
				</Box>
			</Stack>
		</DropdownMenu>
	);
};

export default DropdownFilter;
